import { doc, collection, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'
import { buildEvaluationRecord } from '@/utils/studentEvaluation.js'

// DESTRUCTIVE (dev project only): evaluation record lifecycle. Creates a
// tagged studentEvaluations doc with a fake studentId (does not create a
// student). Asserts create against the rules mirror; read + delete only run
// when create succeeded. Delete is create-roles only (same as create) —
// chairs / psych / 504 coordinator / admin. Not a plain CM (2026-08-30).
// .tmp-smoke-eval-hosted.mjs crud still expected CM create; do not trust it.
export default {
  id: 'studentEvaluationCrud',
  label: 'Evaluation record create/delete',
  description:
    'studentEvaluations lifecycle asserted against the rules for your role. Does not touch student plan fields.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const ref = doc(collection(db, 'studentEvaluations'))
    const expectCreate = RULE_EXPECTATIONS.studentEvaluationCreate(role)
    let created = false

    if (!ctx.schoolId) {
      step.block('create evaluation', 'Select a school before running the evaluation CRUD smoke.')
      return
    }

    const record = buildEvaluationRecord({
      studentId: `smoke-eval-student-${ctx.runId.slice(-8)}`,
      schoolId: ctx.schoolId,
      evalType: 'IEP',
      caseManagerId: ctx.user.uid,
      createdBy: ctx.user.uid,
      dates: { referral: '2026-08-01' }
    })

    try {
      await step(`create evaluation (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(ref, {
            ...record,
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
        )
        created = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — studentEvaluations/${ref.id}`
          : accessDetail(expectCreate, allowed)
      })

      if (!created) {
        step.skip('remaining lifecycle', 'Role cannot create — later steps skipped')
        return
      }

      await step('read evaluation back (expected: allowed — creator/owner)', async () => {
        const { allowed } = await attempt(async () => {
          const snap = await getDoc(ref)
          if (!snap.exists()) throw new Error('Created evaluation not found on read-back')
          const data = snap.data()
          if (data.status) throw new Error('Record stored a rejected status field')
          if (data.plan || data.planStatus) {
            throw new Error('Evaluation record must not carry student plan fields')
          }
        })
        return accessDetail(true, allowed)
      })

      await step('delete evaluation (expected: allowed — create-role delete)', async () => {
        const { allowed } = await attempt(() => deleteDoc(ref))
        return accessDetail(true, allowed)
      })
    } finally {
      await deleteDoc(ref).catch(() => {})
    }
  }
}
